import { isAnyOf } from '@reduxjs/toolkit';
import { lastValueFrom } from 'rxjs';
import { produce } from 'immer';

import {
  connectUserError, connectUserSuccess, connectedUserError,
  connectedUserSuccess, createUserError, createUserSuccess, disconnectUserSuccess
} from '../slices/auth.slice';

import {
  updateCart, updateCartClear, updateCartError, updateCartSuccess,
  updateCartAttributeListSuccess, updateCartAttributeListClear, updateCartAttributeListError,
  updateCartAttributeSuccess, updateCartAttributeClear, updateCartAttributeError,
  payCart, payCartSuccess, payCartError, payCartClear, addCartProductSuccess, selectShippingAddress,
  selectBillingAddress
} from '../slices/cart.slice';

import {
  changeStep,
  checkCartStorageCompatibility,
  connectedSteps,
  disconnectedSteps,
  getLastOrder,
  getLastOrderError,
  getLastOrderSuccess,
  getShippingModesForCart,
  getShippingModesForCartSuccess,
  PaymentStatus, reloadApplePay, resetAddressErrors,
  selectCurrentStepLabel, selectLastOrder,
  setIncompatibleProducts,
  setPaymentStatusInsideCheckout,
  updateStepCart
} from '../slices/checkout.slice';

import {
  hidePageProgress,
  hidePaymentPending,
  openIncompatibleProductsModal,
  showPageProgress,
  showPaymentPending
} from '../slices/gui.slice';
import { getLastStorageOrder } from '../slices/storage.slice';
import { addApiErrorToast, addToast } from '../slices/toast.slice';
import { prevStep, nextStep } from '@/utils/checkoutUtils';
import { isObjectEmpty } from '@/utils/comparisonUtils';
import { CartIncompatibleProduct, CartModel, CartShippingModel } from '@/models';
import { AppListenerEntry, useAppSelector } from '@/redux';
import { redirectToAnotherStep } from '@/redux/thunks/checkout/redirectToAnotherStep';
import { validateCurrentCheckoutStepOrRedirect } from '@/redux/thunks/checkout/validateCurrentCheckoutStepOrRedirect';
import { processPayment } from '@/redux/thunks/checkout/processPayment';
import {
  createPaymentProviderForm,
  initPaymentProvider,
  loadPaymentProvider
} from '@/redux/thunks/checkout/loadPaymentProvider';
import { redirectToPaymentProvider } from '@/redux/thunks/checkout';
import { PENDING_REQUEST, validatePayment } from '@/redux/thunks/checkout/validatePayment';
import { validateDiscountCode } from '@/redux/thunks/checkout/validateDiscountCode';
import { addProductToCartFromCheckout } from '../thunks/checkout/addProductToCartFromCheckout';
import { sendLogs } from '@/redux/thunks/shared/send-logs';
import { LocalStorageService } from '@/services/LocalStorage.service';
import { getShippingList } from '@/redux/thunks/getShippingList';
import { STEPS, STEP_CHANGE, PAYMENT_PLATFORMS, PAYMENT_ERRORS } from '@/constants/checkout';
import { LOG_MSG, LOG_URL } from '@/constants/logger';
import { STORE_ID } from '@/constants/stores';
import { updateCartAttributeByName } from '@/redux/thunks/checkout/updateCartAttributeByName';
import { selectUserAddresses } from '@/redux/slices/user.slice';
import { checkIncompatibleProductsFromCheckout } from '@/redux/thunks/checkout/checkIncompatibleProducts';
import { removeIncompatibleProductsFromCheckout } from '@/redux/thunks/checkout/removeIncompatibleProducts';

export const epicConnectedSteps: AppListenerEntry = {
  matcher: isAnyOf(
    createUserSuccess,
    connectUserSuccess,
    connectedUserSuccess
  ),
  effect: (_action: ReturnType<any>, { dispatch }) => {
    dispatch(connectedSteps());
  }
};

export const epicDisconnectedSteps: AppListenerEntry = {
  matcher: isAnyOf(
    createUserError,
    connectUserError,
    connectedUserError,
    disconnectUserSuccess
  ),
  effect: (_action: ReturnType<any>, { dispatch }) => {
    dispatch(disconnectedSteps());
  }
};

export const epicUpdateStepCart: AppListenerEntry = {
  actionCreator: updateStepCart,
  effect: ({ payload }: ReturnType<typeof updateStepCart>, { getState, dispatch }) => {
    if (!isObjectEmpty(payload.update)) {
      const { cart } = getState();
      dispatch(updateCart({
        content: produce(cart.content as CartModel, (draft) => {
          Object.keys(payload.update).forEach((k) => {
            draft[k] = payload.update[k];
          });
        })
      }));
    }
  }
};

export const epicUpdateCartSuccess: AppListenerEntry = {
  actionCreator: updateCartSuccess,
  effect: (_action: ReturnType<typeof updateCartSuccess>, { dispatch }) => {
    dispatch(updateCartClear());
  }
};

export const epicUpdateCartError: AppListenerEntry = {
  matcher: isAnyOf(
    updateCartError,
    updateCartAttributeError,
    updateCartAttributeListError
  ),
  effect: (action: ReturnType<any>, { dispatch }) => {
    const { error } = action.payload;
    dispatch(addApiErrorToast({ error }));
  }
};

export const epicUpdateCartAttributeSuccess: AppListenerEntry = {
  actionCreator: updateCartAttributeSuccess,
  effect: (_action: ReturnType<typeof updateCartAttributeSuccess>, { dispatch }) => {
    dispatch(updateCartAttributeClear());
  }
};

export const epicUpdateCartAttributeListSuccess: AppListenerEntry = {
  actionCreator: updateCartAttributeListSuccess,
  effect: (_action: ReturnType<typeof updateCartAttributeListSuccess>, { dispatch }) => {
    dispatch(updateCartAttributeListClear());
  }
};

export const epicChangeStep: AppListenerEntry = {
  actionCreator: changeStep,
  effect: (_action: ReturnType<typeof changeStep>, { getState, dispatch }) => {
    const { cart, checkout } = getState();
    const { next, current } = checkout;
    if ((next === STEP_CHANGE.NEXT) && (current.url === STEPS.PAYMODE)) {
      dispatch(payCart({
        content: {
          cart: cart.content,
          fingerprint: cart.fingerprint
        }
      }));
      dispatch(showPaymentPending());
    } else if (next === STEP_CHANGE.PREV) {
      prevStep(checkout);
    } else {
      nextStep(checkout, cart.content);
    }
  }
};

export const epicCartPaid: AppListenerEntry = {
  matcher: isAnyOf(
    payCartSuccess,
    payCartError
  ),
  effect: (_action: ReturnType<any>, { getState, dispatch }) => {
    const { cart } = getState();
    const { error } = cart.status?.pay as { error: any };
    const payModeAction = cart.content?.paiementMode?.action;

    if (
      !(error?.errorCode === PAYMENT_ERRORS.DELAYED_DELIVERY_ERROR) &&
      (payModeAction !== PAYMENT_PLATFORMS.HIPAY &&
        payModeAction !== PAYMENT_PLATFORMS.ADYEN &&
        payModeAction !== PAYMENT_PLATFORMS.ADYEN_APPLEPAY &&
        payModeAction !== PAYMENT_PLATFORMS.ADYEN_TWINT)
    ) {
      dispatch(changeStep({ next: STEPS.REPORT }));
    }
  }
};

export const epicClosingPaymentModal: AppListenerEntry = {
  matcher: isAnyOf(
    payCartSuccess,
    payCartError,
    payCartClear
  ),
  effect: (_action: ReturnType<any>, { dispatch }) => {
    dispatch(hidePageProgress());
  }
};

export const epicClosingPaymentPendingModal: AppListenerEntry = {
  actionCreator: payCartError,
  effect: (_action: ReturnType<any>, { dispatch }) => {
    dispatch(hidePaymentPending());
  }
};

export const epicGetLastOrder: AppListenerEntry = {
  actionCreator: getLastOrder,
  effect: async (_action: ReturnType<typeof getLastOrder>, { dispatch, extra: { orderService } }) => {
    try {
      const order = await lastValueFrom(orderService.getLastOrder());
      dispatch(getLastOrderSuccess({ content: order }));
    } catch (error) {
      dispatch(getLastOrderError({ error }));
    }
  }
};

export const epicgetShippingModesForCart: AppListenerEntry = {
  actionCreator: getShippingModesForCart,
  effect: async ({ payload }: ReturnType<typeof getShippingModesForCart>, { dispatch, extra: { checkoutService } }) => {
    try {
      dispatch(showPageProgress());
      const shippingsList = await lastValueFrom(checkoutService.getShippingModeForCart(payload.storeId)) as CartShippingModel[];
      dispatch(getShippingModesForCartSuccess({ shippingsList }));
      dispatch(hidePageProgress());
    } catch (error) {
      // TODO TYPESCRIPT: error processing
    }
  }
};

export const epicGetLastOrderSuccess: AppListenerEntry = {
  actionCreator: getLastOrderSuccess,
  effect: ({ payload }: ReturnType<typeof getLastOrderSuccess>, { dispatch }) => {
    dispatch(hidePaymentPending());
    if (payload.content?.hasStorageProducts) {
      dispatch(getLastStorageOrder({ orderId: payload.content.id }));
    }
  }
};

export const epicCheckCartStorageCompatibility: AppListenerEntry = {
  actionCreator: checkCartStorageCompatibility,
  effect: async (_action: ReturnType<typeof checkCartStorageCompatibility>, {
    getState, dispatch, extra: { checkoutService }
  }) => {
    try {
      const { root } = getState();

      dispatch(showPageProgress());

      const incompatibleProducts = await lastValueFrom(
        checkoutService.checkCartStorageCompatibility(root.storeConf?.storeId)
      ) as CartIncompatibleProduct[];
      dispatch(setIncompatibleProducts({ incompatibleProducts }));

      dispatch(hidePageProgress());
    } catch (error) {
      dispatch(addApiErrorToast(error));
    }
  }
};

export const checkUserCartWhenRedirecting: AppListenerEntry = {
  matcher: (action) => action.type === redirectToAnotherStep.fulfilled.type,
  effect: async (_action: ReturnType<typeof redirectToAnotherStep>, { dispatch }) => {
    dispatch(validateCurrentCheckoutStepOrRedirect({ type: 'redirectOnly' }));
  }
};

export const checkCartAfterProductQuantityUpdate: AppListenerEntry = {
  matcher: (action) => action.type === addProductToCartFromCheckout.fulfilled.type,
  effect: async (action: ReturnType<typeof addProductToCartFromCheckout.fulfilled>, { dispatch }) => {
    if (action.payload?.products && action.payload?.products?.length === 0) {
      dispatch(validateCurrentCheckoutStepOrRedirect({ type: 'redirectOnly' }));
    }
  }
};

export const epicCreatePaymentProviderForm: AppListenerEntry = {
  matcher: (action) => action.type === loadPaymentProvider.fulfilled.type,
  effect: async (action: ReturnType<typeof loadPaymentProvider.fulfilled>, { dispatch, getState }) => {
    const locale = getState().root.storeConf.locale;
    const countryCode = getState().root.storeConf.codeISO;
    let adyenPubKey = null;
    let isTestEnv = null;

    if (action.payload && action.payload.action === PAYMENT_PLATFORMS.ADYEN_APPLE_QUICK_PAY) {
      adyenPubKey = action.payload.adyenPubKey;
      isTestEnv = action.payload.isTestEnv;
    } else {
      adyenPubKey = getState().cart.content.paiementMode.adyenPubKey;
      isTestEnv = getState().cart.content.paiementMode.isTestEnv;
    }
    try {
      const configuration = formatPaymentProviderData(
        { ...action.payload, clientKey: adyenPubKey, environment: isTestEnv ? 'test' : 'live', locale, countryCode }
      );
      await dispatch(initPaymentProvider(configuration));
      await dispatch(createPaymentProviderForm());
      dispatch(sendLogs(
        { message: LOG_MSG[getState().checkout.paymentProvider.label]?.INIT, url: LOG_URL.PAY, data: configuration }
      ));
    } catch (error) {
      console.error(error);
    }
  }
};

export const epicHandlePaymentResult: AppListenerEntry = {
  actionCreator: setPaymentStatusInsideCheckout,
  effect: async (_, { dispatch, getState }) => {
    const paymentStatus = getState().checkout.paymentStatus as PaymentStatus;

    switch (paymentStatus.status) {
      case 'authorized':
      case 'pending':
        await dispatch(sendLogs(
          { message: LOG_MSG[getState().checkout.paymentProvider.label]?.SUCCESS, url: LOG_URL.PAY, data: paymentStatus }
        ));
        return dispatch(validatePayment(null));
      case 'error':
        await dispatch(sendLogs(
          { message: LOG_MSG[getState().checkout.paymentProvider.label]?.ERROR, url: LOG_URL.PAY, data: paymentStatus }
        ));
        return dispatch(addToast({ msg: 'erreur' }));
      case 'refused':
        await dispatch(sendLogs(
          { message: LOG_MSG[getState().checkout.paymentProvider.label]?.REFUSED, url: LOG_URL.PAY, data: paymentStatus }
        ));
        return dispatch(addToast({ msg: 'paiement refusé' }));
      case 'forwarding':
        await dispatch(sendLogs(
          { message: LOG_MSG[getState().checkout.paymentProvider.label].FORWARDING, url: LOG_URL.PAY, data: paymentStatus }
        ));
        return await redirectToPaymentProvider(paymentStatus.redirectUrl);
    }
  }
};

export const epicReloadPaymentProviderOnDiscountCodeChange: AppListenerEntry = {
  matcher: (action) => action.type === validateDiscountCode.fulfilled.type,
  effect: async (_, { getState, dispatch }) => {
    const isApplePayAvailable = getState().checkout.applePay.isAvailable;
    if (isApplePayAvailable) {
      dispatch(reloadApplePay());
    }
  }
};

export const epicReloadShippingListOnDiscountCodeChange: AppListenerEntry = {
  matcher: (action) => action.type === validateDiscountCode.fulfilled.type,
  effect: async (_, { getState, dispatch }) => {
    const storeId = getState().root.storeConf.storeId;
    const step = getState().checkout.step.label;
    if (step === 'shipping') {
      dispatch(showPageProgress());
      await dispatch(getShippingList({ storeId }));
      dispatch(hidePageProgress());
    }
  }
};

export const epicReloadPaymentProviderOnProductQuantityChange: AppListenerEntry = {
  matcher: (action) => action.type === addProductToCartFromCheckout.fulfilled.type,
  effect: async (_, { getState, dispatch }) => {
    const isApplePayAvailable = getState().checkout.applePay.isAvailable;
    if (isApplePayAvailable) {
      dispatch(reloadApplePay());
    }
  }
};

export const epicReloadApplePayOnChangeCart: AppListenerEntry = {
  actionCreator: addCartProductSuccess,
  effect: async (_, { getState, dispatch }) => {
    const isApplePayAvailable = getState().checkout.applePay.isAvailable;
    if (isApplePayAvailable) {
      dispatch(reloadApplePay());
    }
  }
};

export const epicValidatePaymentAfterThePaymentProcess: AppListenerEntry = {
  matcher: (action) => action.type === validateCurrentCheckoutStepOrRedirect.fulfilled.type,
  effect: async (action, { dispatch }) => {
    if (action.payload?.processPayment) {
      await dispatch(processPayment());
    }
  }
};

export const validateStepLoginAfterTheUserLoggedIn: AppListenerEntry = {
  matcher: (action) => connectOrRegisterActions.includes(action.type),
  effect: async (action, { dispatch, getState }) => {
    if (selectCurrentStepLabel(getState()) === STEPS.LOGIN) {
      await dispatch(validateCurrentCheckoutStepOrRedirect({ type: 'validateAndRedirect' }));
    }
  }
};

export const deleteLocalStorageCartIdAfterSuccessOrErrorOfThePaymentValidation: AppListenerEntry = {
  matcher: (action) => validatePayment.rejected.type === action.type,
  effect: async (action, { getState }) => {
    const orderId = selectLastOrder(getState()).id;
    if (
      !isObjectEmpty(LocalStorageService.get(orderId)) && LocalStorageService.get(orderId) !== '' &&
      action.payload.cause && action.payload.cause !== PENDING_REQUEST
    ) {
      LocalStorageService.remove(orderId);
    }
  }
};

export const deleteLocalStorageCartIdAfterFulfilledPayment: AppListenerEntry = {
  matcher: (action) => validatePayment.fulfilled.type === action.type,
  effect: async (_action, { getState }) => {
    const orderId = selectLastOrder(getState()).id;
    if (!isObjectEmpty(LocalStorageService.get(orderId)) && LocalStorageService.get(orderId) !== '') {
      LocalStorageService.remove(orderId);
    }
  }
};

export const epicCheckIncompatibleProducts: AppListenerEntry = {
  matcher: (action) => action.type === checkIncompatibleProductsFromCheckout.fulfilled.type,
  effect: async (_, { getState, dispatch }) => {
    if (getState().checkout.incompatibleSpiritProducts?.length > 0) {
      dispatch(openIncompatibleProductsModal());
    }
  }
};

export const epicRemoveIncompatibleProducts: AppListenerEntry = {
  matcher: (action) => action.type === removeIncompatibleProductsFromCheckout.fulfilled.type,
  effect: async (_, { dispatch }) => {
    dispatch(validateCurrentCheckoutStepOrRedirect({ type: 'validateOnly' }));
  }
};
export const updateAddressInCart: AppListenerEntry = {
  actionCreator: resetAddressErrors,
  effect: (_, { getState, dispatch }) => {
    const isBilling = selectCurrentStepLabel(getState()) === 'billing';
    const shippingAddress = selectShippingAddress(getState());
    const billingAddress = selectBillingAddress(getState());

    const erroredAddress = isBilling ? billingAddress : shippingAddress;

    const userAddresses = selectUserAddresses(getState());
    const defaultAddress = userAddresses.find((address) => address.isDefault) || userAddresses[0];

    if (erroredAddress.id !== defaultAddress.id) {
      dispatch(updateCartAttributeByName({ attributeName: isBilling ? 'billing' : 'shipping', newValue: defaultAddress }));
    }
  }
};

const connectOrRegisterActions: string[] = [
  connectUserSuccess.type, createUserSuccess.type, connectedUserSuccess.type
];

const formatPaymentProviderData = (paymentProviderData: unknown) => {
  if (paymentProviderData && Object.keys(paymentProviderData).length === 1) {
    return;
  }

  const {
    id,
    sessionData,
    clientKey,
    locale,
    returnUrl,
    reference,
    showPayButton,
    environment
  } = paymentProviderData as any;

  const result: any = {
    session: {
      id,
      sessionData,
    },
    clientKey,
    locale,
    returnUrl,
    reference,
    showPayButton,
    environment,
    countryCode: 'FR'
  };

  return result;
};
